/**
 * @fileoverview Hook for claiming public links after user signup.
 *
 * When a user creates anonymous links on the landing page and then signs up,
 * this hook automatically claims those links to transfer ownership.
 *
 * The hook:
 * 1. Checks localStorage for recent links created before signup
 * 2. Sends the short codes to the claim API endpoint
 * 3. Clears claimed links from localStorage
 * 4. Shows toast notifications for the results
 */

import { useEffect, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuth } from './use-auth';
import { apiFetch } from '@/client/lib/api';

const STORAGE_KEY = 'arml_recent_links';
const CLAIM_ATTEMPTED_KEY = 'arml_claim_attempted';

interface RecentLink {
  shortUrl: string;
  shortCode: string;
  longUrl: string;
  qrCodeUrl: string;
  dateCreated: string;
  expiresAt?: string;
}

interface ClaimResult {
  claimed: string[];
  failed: string[];
  message: string;
}

/**
 * Get recent links from localStorage.
 */
function getRecentLinks(): RecentLink[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

/**
 * Clear claimed links from localStorage.
 */
function clearClaimedLinks(claimedCodes: string[]): void {
  try {
    const links = getRecentLinks();
    const remaining = links.filter((l) => !claimedCodes.includes(l.shortCode));
    if (remaining.length > 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(remaining));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Ignore errors
  }
}

/**
 * Check if we've already attempted to claim links in this session.
 */
function hasAttemptedClaim(userId: string): boolean {
  try {
    const attempted = localStorage.getItem(CLAIM_ATTEMPTED_KEY);
    return attempted === userId;
  } catch {
    return false;
  }
}

/**
 * Mark that we've attempted to claim links.
 */
function markClaimAttempted(userId: string): void {
  try {
    localStorage.setItem(CLAIM_ATTEMPTED_KEY, userId);
  } catch {
    // Ignore errors
  }
}

/**
 * Hook that automatically claims public links when a user logs in.
 *
 * This should be called once in a component that's rendered after login
 * (e.g., the dashboard or protected route wrapper).
 */
export function useClaimLinks() {
  const { user, isAuthenticated, isLoading } = useAuth();
  const hasRun = useRef(false);

  const claimMutation = useMutation({
    mutationFn: async (shortCodes: string[]): Promise<ClaimResult> => {
      const response = await apiFetch('/api/links/claim', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shortCodes }),
      });

      if (!response.ok) {
        throw new Error('Failed to claim links');
      }

      return response.json();
    },
    onSuccess: (result) => {
      if (result.claimed.length > 0) {
        clearClaimedLinks(result.claimed);
        toast.success(
          `Claimed ${result.claimed.length} link${result.claimed.length === 1 ? '' : 's'} from before you signed up!`,
          {
            description: 'These links are now permanent and visible in your dashboard.',
            duration: 5000,
          }
        );
      }
    },
    onError: () => {
      // Silently fail - don't bother the user with claim errors
      console.error('Failed to claim links');
    },
  });

  useEffect(() => {
    // Only run once per mount and only when authenticated
    if (hasRun.current || isLoading || !isAuthenticated || !user) {
      return;
    }

    // Don't run if we've already attempted for this user
    if (hasAttemptedClaim(user.id)) {
      return;
    }

    // Get links from localStorage
    const recentLinks = getRecentLinks();
    if (recentLinks.length === 0) {
      return;
    }

    // Extract short codes
    const shortCodes = recentLinks.map((l) => l.shortCode);

    // Mark as run and attempt claim
    hasRun.current = true;
    markClaimAttempted(user.id);
    claimMutation.mutate(shortCodes);
  }, [isAuthenticated, isLoading, user, claimMutation]);

  return {
    isPending: claimMutation.isPending,
    isSuccess: claimMutation.isSuccess,
    claimedCount: claimMutation.data?.claimed?.length ?? 0,
  };
}
